import React from 'react';
import styled from 'styled-components';
import { FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import { FaWhatsapp, FaInstagram, FaXTwitter, FaFacebookF } from 'react-icons/fa6';
import { useContactInfo } from '../hooks/useContactInfo';
import { usePageMetadata } from '../hooks/usePageMetadata';

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 40px 20px 64px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Header = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
`;

const LastUpdated = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #5efba9;
  color: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const CardBody = styled.div`
  font-size: 15px;
  color: #4b5563;
  line-height: 1.7;

  a {
    color: #0f172a;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactItem = styled.li`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  color: #374151;

  svg {
    flex-shrink: 0;
    color: #5efba9;
    font-size: 20px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const SocialButton = styled.a`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #0f172a;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.25);
    background: #5efba9;
    color: #0f172a;
  }
`;

const Loader = styled.div`
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  text-align: center;
`;

const formatDate = (value) => {
  if (!value) return null;
  try {
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_err) {
    return null;
  }
};

const Contact = () => {
  const { contactInfo, loading: contactLoading, error: contactError } = useContactInfo();
  const { metadata, loading: metadataLoading } = usePageMetadata('contact');
  const isLoading = contactLoading || metadataLoading;

  const socialLinks = [
    { icon: <FaWhatsapp />, label: 'WhatsApp', href: contactInfo?.whatsapp_url },
    { icon: <FaInstagram />, label: 'Instagram', href: contactInfo?.instagram_url },
    { icon: <FaXTwitter />, label: 'X (Twitter)', href: contactInfo?.twitter_url },
    { icon: <FaFacebookF />, label: 'Facebook', href: contactInfo?.facebook_url },
  ].filter(link => link.href);

  return (
    <Container>
      <Header>
        <Title>{metadata?.title_en ?? 'Contact Us'}</Title>
        {metadata?.description_en && (
          <Description>{metadata.description_en}</Description>
        )}
        {metadata?.last_updated && (
          <LastUpdated>Last updated {formatDate(metadata.last_updated)}</LastUpdated>
        )}
      </Header>

      {isLoading ? (
        <Loader>Loading contact information...</Loader>
      ) : contactError ? (
        <ErrorMessage>{contactError}</ErrorMessage>
      ) : contactInfo ? (
        <>
          <ContentGrid>
            <Card>
              <CardHeader>
                <IconWrapper>
                  <FiMail />
                </IconWrapper>
                <CardTitle>Contact Details</CardTitle>
              </CardHeader>
              <CardBody>
                <ContactList>
                  <ContactItem>
                    <FiMail />
                    <span>
                      Email<br />
                      <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
                    </span>
                  </ContactItem>
                  {contactInfo.phone && (
                    <ContactItem>
                      <FiPhone />
                      <span>
                        Phone<br />
                        <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
                      </span>
                    </ContactItem>
                  )}
                  {contactInfo.address_en && (
                    <ContactItem>
                      <FiMapPin />
                      <span>
                        Address<br />
                        {contactInfo.address_en}
                      </span>
                    </ContactItem>
                  )}
                </ContactList>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <IconWrapper>
                  <FiClock />
                </IconWrapper>
                <CardTitle>Working Hours</CardTitle>
              </CardHeader>
              <CardBody>
                {contactInfo.working_hours_en ? (
                  <p style={{ margin: 0 }}>{contactInfo.working_hours_en}</p>
                ) : (
                  <p style={{ margin: 0 }}>We are here to help you throughout the week.</p>
                )}

                {socialLinks.length > 0 && (
                  <>
                    <p style={{ margin: '24px 0 12px', fontWeight: 600 }}>Social Media</p>
                    <SocialLinks>
                      {socialLinks.map(link => (
                        <SocialButton key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" aria-label={link.label}>
                          {link.icon}
                        </SocialButton>
                      ))}
                    </SocialLinks>
                  </>
                )}
              </CardBody>
            </Card>
          </ContentGrid>
        </>
      ) : (
        <ErrorMessage>No contact information available.</ErrorMessage>
      )}
    </Container>
  );
};

export default Contact;

